'use client';

import { EventClickElement } from '@tuic/tracking';
import { DashboardCard } from '@tuic/web-compositions/shared/DashboardCard';
import { useState } from 'react';

import { useXiCafeStatus } from '../hooks/use-xi-cafe-status';
import { getXiCafeCaptivePortalUrl } from '../lib/captive-portal-url';

import { XiCafeConnectionStatusSymbol } from './xi-cafe-connection-status-symbol';
import { XiCafePortalDialog } from './xi-cafe-portal-dialog';

import type { Locale } from '@tuic/i18n';
import type { DashboardCardProps } from '@tuic/web-compositions/shared/DashboardCard';
import type { FC } from 'react';

export type XiCafeInternetCardProps = {
  locale: Locale;
  mpi: string | null;
  token: string | null;
  dashboardCard?: DashboardCardProps;
};

/**
 * XiCafe internet card.
 *
 * Everything runs client-side, like the native app: the tariff status comes
 * from a direct `bookings/list` call (MPI + JWT), and the captive portal opens
 * in an iframe loaded straight from the portal URL with the `?token=` param.
 * The dashboard's Next.js server is not on the ship network, so it cannot reach
 * the XiCafe APIs — only the on-ship browser can.
 */
export const XiCafeInternetCard: FC<XiCafeInternetCardProps> = ({
  locale,
  mpi,
  token,
  dashboardCard,
}) => {
  const [open, setOpen] = useState(false);
  const { status } = useXiCafeStatus({ mpi, token });

  return (
    <>
      <DashboardCard
        {...dashboardCard}
        asChild
        tag={<XiCafeConnectionStatusSymbol status={status} />}
      >
        <button
          onClick={() => setOpen(true)}
          data-track-click-element={EventClickElement.Card}
          data-track-click-label="xi-cafe-internet-card"
        />
      </DashboardCard>
      <XiCafePortalDialog
        open={open}
        onOpenChange={setOpen}
        src={getXiCafeCaptivePortalUrl(token, locale)}
      />
    </>
  );
};
